"use client";

import { MessageSquare, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, Send, ArrowDownLeft, ArrowUpRight, Eye, Image as ImageIcon } from "lucide-react";
import React, { useState } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/app/_module/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/app/_module/components/ui/table";
import {
    Dialog,
    ResponsiveDialogContent,
    DialogHeader,
    DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { DataPagination, type PaginationMeta } from "@/app/_module/components/ui/data-pagination";
import { useSmsConversation } from "@/app/_module/contexts/SmsConversationContext";
import { getSmsFailureReason, getSmsSentUnconfirmedReason } from "@/app/_module/utils/smsFailureReason";

// Extended type to handle both frontend SMSRecord and backend SMS data
interface SmsRecordItem {
    id: string;
    phoneNumber: string;
    contactName?: string;
    // Frontend format
    direction?: 'inbound' | 'outbound';
    message?: string;
    status?: 'sent' | 'delivered' | 'failed' | 'pending';
    timestamp?: Date | string;
    // Backend format
    messageType?: 'inbound' | 'outbound';
    messageContent?: string;
    messageStatus?: string;
    messageDate?: Date | string;
    // MMS media URLs (both formats)
    mediaUrls?: string[];
    media_urls?: string[];
    // Failure details for failed/undelivered messages
    errorCode?: string | null;
    errorMessage?: string | null;
}

interface LeadSmsHistoryProps {
    leadId: string;
    leadName?: string;
    leadPhone?: string;
    smsRecords?: SmsRecordItem[];
    isLoading?: boolean;
    onRefresh?: () => Promise<void>;
    paginationMeta?: PaginationMeta;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    onOpenSmsWidget?: () => void;
    embedded?: boolean; // When true, hide outer Card wrapper
}

// Format phone number to (xxx) xxx-xxxx format
const formatPhoneNumber = (phone: string): string => {
    if (!phone) return "";
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, "");
    // Handle 10-digit numbers
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    // Handle 11-digit numbers (with country code)
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
        return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    // Return original if format doesn't match
    return phone;
};

// Helper to get direction from SMS record (handles both frontend and backend formats)
const getDirection = (sms: SmsRecordItem): 'inbound' | 'outbound' => {
    return sms.direction || sms.messageType || 'outbound';
};

// Helper to get message content from SMS record
const getMessage = (sms: SmsRecordItem): string => {
    return sms.message || sms.messageContent || '';
};

// Helper to get status from SMS record
const getStatus = (sms: SmsRecordItem): string => {
    return sms.status || sms.messageStatus || 'unknown';
};

// Helper to get timestamp from SMS record
const getTimestamp = (sms: SmsRecordItem): Date | string => {
    return sms.timestamp || sms.messageDate || new Date();
};

// Helper to get media URLs from SMS record
const getMediaUrls = (sms: SmsRecordItem): string[] => {
    return sms.mediaUrls || sms.media_urls || [];
};

// Normalize status to standard values
type NormalizedStatus = 'sent' | 'delivered' | 'failed' | 'pending' | 'unknown';
const normalizeStatus = (status: string): NormalizedStatus => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'delivered') return 'delivered';
    if (statusLower === 'sent' || statusLower === 'sending') return 'sent';
    if (statusLower === 'failed' || statusLower === 'undelivered') return 'failed';
    if (statusLower === 'pending' || statusLower === 'queued' || statusLower === 'scheduled') return 'pending';
    if (statusLower === 'received') return 'delivered'; // Inbound messages that were received
    return 'unknown';
};

export const LeadSmsHistory: React.FC<LeadSmsHistoryProps> = function ({
    leadId,
    leadName,
    leadPhone,
    smsRecords = [],
    isLoading = false,
    onRefresh,
    paginationMeta,
    onPageChange,
    onPageSizeChange,
    onOpenSmsWidget,
    embedded = false,
}) {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [expandedSmsId, setExpandedSmsId] = useState<string | null>(null);
    const [viewMessageDialog, setViewMessageDialog] = useState<{ open: boolean; sms: SmsRecordItem | null }>({
        open: false,
        sms: null,
    });

    // Use the SMS conversation context to open the chat widget
    const { startConversation } = useSmsConversation();

    // Handler to open SMS chat widget with lead info
    const handleOpenSmsWidget = async (phoneNumber?: string) => {
        // Use the provided phone number or fall back to leadPhone prop or first SMS record's phone
        const phone = phoneNumber || leadPhone || (smsRecords.length > 0 ? smsRecords[0].phoneNumber : '');
        const name = leadName || (smsRecords.length > 0 ? smsRecords[0].contactName : undefined) || 'Unknown';

        if (phone && leadId) {
            await startConversation({
                leadId,
                leadName: name,
                phoneNumber: phone,
            });
        } else if (onOpenSmsWidget) {
            // Fallback to the old callback if context can't be used
            onOpenSmsWidget();
        }
    };

    const handleRefresh = async () => {
        if (onRefresh && !isRefreshing) {
            setIsRefreshing(true);
            try {
                await onRefresh();
            } finally {
                setIsRefreshing(false);
            }
        }
    };

    const getStatusIcon = (status: NormalizedStatus) => {
        switch (status) {
            case "delivered":
                return <CheckCircle className="h-4 w-4 text-green-600" />;
            case "sent":
                return <Send className="h-4 w-4 text-blue-600" />;
            case "failed":
                return <XCircle className="h-4 w-4 text-red-600" />;
            case "pending":
                return <AlertCircle className="h-4 w-4 text-yellow-600" />;
            default:
                return <MessageSquare className="h-4 w-4 text-gray-600" />;
        }
    };

    const getStatusBadge = (status: NormalizedStatus) => {
        const variants: Record<string, string> = {
            delivered: "bg-green-100 text-green-800",
            sent: "bg-blue-100 text-blue-800",
            failed: "bg-red-100 text-red-800",
            pending: "bg-yellow-100 text-yellow-800",
            unknown: "bg-gray-100 text-gray-800",
        };

        return (
            <Badge className={variants[status] || "bg-gray-100 text-gray-800"}>
                {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
            </Badge>
        );
    };

    const getDirectionIcon = (direction: 'inbound' | 'outbound') => {
        if (direction === "inbound") {
            return <ArrowDownLeft className="h-4 w-4 text-green-600" />;
        }
        return <ArrowUpRight className="h-4 w-4 text-blue-600" />;
    };

    const formatDate = (dateString: string | Date) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    // Truncate message for preview
    const getMessagePreview = (message: string, maxLength: number = 50): string => {
        if (!message) return "";
        if (message.length <= maxLength) return message;
        return message.substring(0, maxLength) + "...";
    };

    const toggleSmsExpanded = (smsId: string) => {
        setExpandedSmsId(expandedSmsId === smsId ? null : smsId);
    };

    if (isLoading) {
        const loadingContent = (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading SMS history...</span>
            </div>
        );

        if (embedded) return loadingContent;

        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        SMS History
                    </CardTitle>
                </CardHeader>
                <CardContent>{loadingContent}</CardContent>
            </Card>
        );
    }

    // Compact card-based view for embedded mode
    if (embedded) {
        if (smsRecords.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <MessageSquare className="h-10 w-10 text-gray-400 mb-3" />
                    <p className="text-sm text-gray-600">No SMS messages yet</p>
                </div>
            );
        }

        return (
            <div className="space-y-2">
                {smsRecords.map((sms) => {
                    const direction = getDirection(sms);
                    const status = normalizeStatus(getStatus(sms));
                    const message = getMessage(sms);
                    const timestamp = getTimestamp(sms);
                    const mediaUrls = getMediaUrls(sms);

                    return (
                        <div
                            key={sms.id}
                            className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleOpenSmsWidget(sms.phoneNumber)}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                    {getDirectionIcon(direction)}
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">
                                            {formatPhoneNumber(sms.phoneNumber)}
                                        </p>
                                        <div className="flex items-center gap-1">
                                            {mediaUrls.length > 0 && (
                                                <span className="inline-flex items-center gap-0.5 text-xs text-blue-600">
                                                    <ImageIcon className="h-3 w-3" />
                                                    {mediaUrls.length}
                                                </span>
                                            )}
                                            <p className="text-xs text-gray-500 line-clamp-1">
                                                {message && message !== '[MMS Attachment]'
                                                    ? getMessagePreview(message, 40)
                                                    : mediaUrls.length > 0
                                                        ? 'Image attachment'
                                                        : getMessagePreview(message, 40)
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="shrink-0 flex flex-col items-end gap-1">
                                    {getStatusBadge(status)}
                                    {(status === 'failed' || status === 'sent') && (() => {
                                        // 'sent' is NOT a failure — the carrier took the message and
                                        // never sent a receipt. Neutral grey, never red.
                                        const isUnconfirmed = status === 'sent';
                                        const reason = isUnconfirmed
                                            ? getSmsSentUnconfirmedReason()
                                            : getSmsFailureReason(sms.errorCode, sms.errorMessage);
                                        return reason ? (
                                            <span className={`text-xs text-right max-w-[10rem] line-clamp-2 ${isUnconfirmed ? 'text-muted-foreground' : 'text-red-600'}`} title={reason.hint}>
                                                {reason.label}
                                            </span>
                                        ) : null;
                                    })()}
                                </div>
                            </div>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t">
                                <p className="text-xs text-gray-500">
                                    {formatDate(timestamp)}
                                </p>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenSmsWidget(sms.phoneNumber);
                                    }}
                                    className="h-6 px-2 text-xs"
                                >
                                    <Eye className="h-3 w-3 mr-1" />
                                    View
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    const mainContent = (
        <>
            {smsRecords.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <MessageSquare className="h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No SMS messages yet</h3>
                        <p className="text-gray-600 mb-4">
                            SMS history will appear here once you start messaging this lead.
                        </p>
                    </div>
                ) : (
                    <>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Phone</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Message</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {smsRecords.map((sms) => {
                                    const isExpanded = expandedSmsId === sms.id;
                                    const direction = getDirection(sms);
                                    const status = normalizeStatus(getStatus(sms));
                                    const message = getMessage(sms);
                                    const timestamp = getTimestamp(sms);
                                    const mediaUrls = getMediaUrls(sms);

                                    return (
                                        <React.Fragment key={sms.id}>
                                            <TableRow
                                                className="cursor-pointer hover:bg-gray-50"
                                                onClick={() => toggleSmsExpanded(sms.id)}
                                            >
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {getDirectionIcon(direction)}
                                                        <span className="capitalize">
                                                            {direction === "inbound" ? "Inbound" : "Outbound"}
                                                        </span>
                                                        {mediaUrls.length > 0 && (
                                                            <span className="inline-flex items-center gap-0.5 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                                                <ImageIcon className="h-3 w-3" />
                                                                {mediaUrls.length}
                                                            </span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="font-medium">
                                                    {formatPhoneNumber(sms.phoneNumber)}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {getStatusIcon(status)}
                                                        {getStatusBadge(status)}
                                                    </div>
                                                    {(status === 'failed' || status === 'sent') && (() => {
                                                        const isUnconfirmed = status === 'sent';
                                                        const reason = isUnconfirmed
                                                            ? getSmsSentUnconfirmedReason()
                                                            : getSmsFailureReason(sms.errorCode, sms.errorMessage);
                                                        return reason ? (
                                                            <div className={`mt-1 text-xs ${isUnconfirmed ? 'text-muted-foreground' : 'text-red-600'}`} title={reason.hint}>
                                                                {reason.label}
                                                            </div>
                                                        ) : null;
                                                    })()}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="h-4 w-4 text-gray-400" />
                                                        {formatDate(timestamp)}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-gray-700">
                                                        {message && message !== '[MMS Attachment]'
                                                            ? getMessagePreview(message)
                                                            : mediaUrls.length > 0
                                                                ? 'Image attachment'
                                                                : getMessagePreview(message)
                                                        }
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                // Open SMS chat widget with this record's phone number
                                                                handleOpenSmsWidget(sms.phoneNumber);
                                                            }}
                                                            className="gap-1"
                                                        >
                                                            <Eye className="h-3 w-3" />
                                                            View
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                            {isExpanded && (
                                                <TableRow>
                                                    <TableCell colSpan={6} className="bg-gray-50/50 p-4">
                                                        <div className="space-y-2">
                                                            {/* MMS Media attachments */}
                                                            {mediaUrls.length > 0 && (
                                                                <div className="space-y-2">
                                                                    <span className="text-sm font-medium text-gray-600">Attachments:</span>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {mediaUrls.map((url, index) => (
                                                                            <a
                                                                                key={index}
                                                                                href={url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="block"
                                                                            >
                                                                                <img
                                                                                    src={url}
                                                                                    alt={`Attachment ${index + 1}`}
                                                                                    className="max-w-48 max-h-32 rounded-md border object-contain hover:opacity-90 transition-opacity"
                                                                                    onError={(e) => {
                                                                                        const target = e.target as HTMLImageElement;
                                                                                        target.style.display = 'none';
                                                                                        target.nextElementSibling?.classList.remove('hidden');
                                                                                    }}
                                                                                />
                                                                                <div className="hidden items-center gap-2 p-2 bg-gray-100 rounded-md text-gray-600">
                                                                                    <ImageIcon className="h-4 w-4" />
                                                                                    <span className="text-xs">View Attachment</span>
                                                                                </div>
                                                                            </a>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {/* Message text */}
                                                            {message && message !== '[MMS Attachment]' && (
                                                                <>
                                                                    <div className="flex items-start gap-2">
                                                                        <span className="text-sm font-medium text-gray-600">Message:</span>
                                                                    </div>
                                                                    <p className="text-sm text-gray-800 whitespace-pre-wrap bg-white p-3 rounded-lg border">
                                                                        {message}
                                                                    </p>
                                                                </>
                                                            )}
                                                            {sms.contactName && (
                                                                <div className="flex items-center gap-2 text-sm">
                                                                    <span className="font-medium text-gray-600">Contact:</span>
                                                                    <span>{sms.contactName}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>

                    {paginationMeta && onPageChange && !embedded && (
                        <DataPagination
                            meta={paginationMeta}
                            onPageChange={onPageChange}
                            onPageSizeChange={onPageSizeChange}
                            isLoading={isLoading}
                            showPageSize={true}
                        />
                    )}
                    </>
                )}

                {/* View Message Dialog */}
                <Dialog open={viewMessageDialog.open} onOpenChange={(open) => setViewMessageDialog({ open, sms: open ? viewMessageDialog.sms : null })}>
                    <ResponsiveDialogContent size="md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <MessageSquare className="h-5 w-5" />
                                SMS Message
                            </DialogTitle>
                        </DialogHeader>
                        {viewMessageDialog.sms && (() => {
                            const dialogSms = viewMessageDialog.sms;
                            const dialogDirection = getDirection(dialogSms);
                            const dialogStatus = normalizeStatus(getStatus(dialogSms));
                            const dialogMessage = getMessage(dialogSms);
                            const dialogTimestamp = getTimestamp(dialogSms);

                            return (
                                <div className="space-y-4 py-4">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="font-medium text-gray-600">Direction:</span>
                                            <div className="flex items-center gap-2 mt-1">
                                                {getDirectionIcon(dialogDirection)}
                                                <span className="capitalize">{dialogDirection}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">Status:</span>
                                            <div className="mt-1">
                                                {getStatusBadge(dialogStatus)}
                                            </div>
                                            {(dialogStatus === 'failed' || dialogStatus === 'sent') && (() => {
                                                const isUnconfirmed = dialogStatus === 'sent';
                                                const reason = isUnconfirmed
                                                    ? getSmsSentUnconfirmedReason()
                                                    : getSmsFailureReason(dialogSms.errorCode, dialogSms.errorMessage);
                                                return reason ? (
                                                    <div className="mt-1.5">
                                                        <p className={`text-xs font-medium ${isUnconfirmed ? 'text-muted-foreground' : 'text-red-600'}`} title={reason.hint}>
                                                            {reason.label}
                                                        </p>
                                                        <p className={`mt-0.5 text-xs ${isUnconfirmed ? 'text-muted-foreground' : 'text-red-600/90'}`}>
                                                            {reason.hint}
                                                        </p>
                                                    </div>
                                                ) : null;
                                            })()}
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">Phone:</span>
                                            <p className="mt-1">{formatPhoneNumber(dialogSms.phoneNumber)}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-600">Date:</span>
                                            <p className="mt-1">{formatDate(dialogTimestamp)}</p>
                                        </div>
                                        {dialogSms.contactName && (
                                            <div className="col-span-2">
                                                <span className="font-medium text-gray-600">Contact:</span>
                                                <p className="mt-1">{dialogSms.contactName}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-600 text-sm">Message:</span>
                                        <p className="mt-2 text-gray-800 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg border">
                                            {dialogMessage}
                                        </p>
                                    </div>
                                </div>
                            );
                        })()}
                    </ResponsiveDialogContent>
                </Dialog>
        </>
    );

    if (embedded) return mainContent;

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        SMS History ({paginationMeta?.totalItems ?? smsRecords.length})
                    </CardTitle>
                    {onRefresh && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleRefresh}
                            disabled={isRefreshing || isLoading}
                            className="gap-2"
                        >
                            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                            {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                {mainContent}
            </CardContent>
        </Card>
    );
};

export default LeadSmsHistory;
